import _extends from "@babel/runtime/helpers/extends";
import { Box, Button, Heading, Loader, TextButton } from '@wix/design-system';
import { AlternativesError } from './AlternativesError';
import { AlternativesSkeleton } from './AlternativesSkeleton';
import { DomainSearchSuiteModule, SearchRecommendationLayout } from '@wix/domains-search-suite';
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
import React from 'react';
import { AnonymousFooter } from '../../../../Components/AnonymousFooter/AnonymousFooter';
import { LazyRecommendationLabel } from '../../../../Components/LazyRecommedationLabel/LazyRecommendationLabel';
import { WhoisVoucherBanner } from '../../../../Components/VoucherBanner';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { useWhoisMobileOptimizations } from '../../../../hooks/useWhoisMobileOptimizations';
import { useMatchingLabels } from '../../../../hooks/useMatchingLabels';
import { useRecommendations } from '../../../../hooks/useRecommendations';
import { useResultsPageOfferingsBi } from '../../../../hooks/useResultsPageOfferingsBi';
import { DomainSearchGateModule } from '../../../../module/domainSearchGate.module';
import s from '../whoisResultsPage.scss';

/**
 * WHOIS-specific override of the mobile actions row: pricing inline with
 * the Get It button on a single `space-between` row, matching the Figma
 * `domainSuggestionsCard` layout. WHOIS doesn't render an AI Insights
 * button, so the slot only handles `pricing` and `getItButton`.
 */
const InlineMobileActions = _ref => {
  let {
    pricing,
    getItButton
  } = _ref;
  return /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "space-between",
    verticalAlign: "middle",
    width: "100%"
  }, pricing, getItButton);
};

// Inject our own className onto the card outer wrapper so we can target it
// from CSS — the original `searchRecommendation` class from
// domains-search-suite is hashed to a short module-local name in production
// (e.g. "iqrywG"), so `[class*="searchRecommendation"]` selectors don't
// match. This wrapper preserves the existing behavior and adds `.cardWrapper`.
const CardHoverBox = _ref2 => {
  let {
    className,
    children,
    ...props
  } = _ref2;
  return /*#__PURE__*/React.createElement(Box, _extends({}, props, {
    className: `${className ?? ''} ${s.cardWrapper}`
  }), children);
};

// Wrap the title with our own className so we can force 20px font-size on
// the inner Text/Heading regardless of which TitleDisplay branch renders.
const CardTitleDisplay = props => /*#__PURE__*/React.createElement("span", {
  className: s.cardDomainTitle
}, /*#__PURE__*/React.createElement(SearchRecommendationLayout.TitleDisplay, props));

// Laura #2c + #2d: cards span the full panel width (no padding) and the
// vertical gap between the title-group Box and the MobileActions row is
// tightened to 12px (was SP4). The divider's own margin handles the
// between-cards spacing.
const NoPadCardContainer = _ref3 => {
  let {
    children,
    ...props
  } = _ref3;
  return /*#__PURE__*/React.createElement(SearchRecommendationLayout.SearchRecommendationContainer, _extends({}, props, {
    padding: "0",
    gap: "12px",
    overflow: "visible"
  }), children);
};

// Laura #2a + #2d: render the divider without any className (the SDK's
// hover-driven `.hidden` class can never apply, killing the flicker) and
// wrap it with a Box that carries 24px top/bottom margin so cards now
// derive their vertical separation from the divider rather than the
// per-card padding (which we removed in `NoPadCardContainer`).
const StableDivider = () => /*#__PURE__*/React.createElement("div", {
  className: s.cardDivider
}, /*#__PURE__*/React.createElement(SearchRecommendationLayout.Divider, null));

// Laura #2e: GetIt button rendered at `size='large'` instead of medium.
// Tag the button with our own className so we can also wire up scoped
// hover styles if needed.
const LargeGetItButton = _ref4 => {
  let {
    children,
    domainName: _domainName,
    ...props
  } = _ref4;
  return /*#__PURE__*/React.createElement(Button, _extends({
    size: "large",
    skin: "dark"
  }, props), children);
};
const INITIAL_RESULTS_COUNT = 5;
export const AlternativesPanel = _ref5 => {
  let {
    voucherBannerRef,
    reportOfferingsBi = false
  } = _ref5;
  const {
    t
  } = DomainSearchGateModule.internals.useTranslations();
  const isMobile = useIsMobile();
  const mobileOptimizations = useWhoisMobileOptimizations();
  const {
    data: purchasePermissions
  } = DomainsApiClient.hooks.useGetPurchasePermissions({});
  const {
    resolveActionForSuggestion
  } = DomainSearchSuiteModule.SDK.useSuggestionActionResolver();
  const {
    recommendations,
    isLoading,
    hasNextPage: hasMore,
    fetchNextPage,
    isFetchingNextPage,
    taxesNotIncluded,
    error,
    onRetry,
    isPricingLoading
  } = useRecommendations({
    limit: INITIAL_RESULTS_COUNT
  });
  const {
    labels: matchLabels,
    isLoading: isLoadingLabels
  } = useMatchingLabels(recommendations);
  useResultsPageOfferingsBi({
    recommendations,
    isLoading,
    enabled: reportOfferingsBi
  });
  if (isLoading && recommendations.length === 0) {
    return /*#__PURE__*/React.createElement(AlternativesSkeleton, null);
  }
  if (error === 'NetworkIssue' || error === 'TechnicalIssue') {
    return /*#__PURE__*/React.createElement(AlternativesError, {
      onRetry: onRetry
    });
  }
  return /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "SP4",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Box, {
    className: s.alternativesCard,
    direction: "vertical",
    gap: "32px",
    width: "100%",
    backgroundColor: "white",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "D60",
    borderRadius: "12px",
    overflow: mobileOptimizations ? 'hidden' : undefined
  }, /*#__PURE__*/React.createElement(WhoisVoucherBanner, {
    ref: voucherBannerRef
  }), /*#__PURE__*/React.createElement(Box, {
    align: mobileOptimizations ? 'center' : 'left'
  }, /*#__PURE__*/React.createElement(Heading, {
    size: "large",
    as: "h2"
  }, t(isMobile ? 'domain_search_gate.whois_results.mobile.suggestions_card.subtitle' : 'domain_search_gate.whois_results.suggestions_card.title'))), /*#__PURE__*/React.createElement(DomainSearchSuiteModule.SDK.Components.SearchRecommendationList, {
    disabled: !(purchasePermissions != null && purchasePermissions.hasPermission),
    defaultNonHovered: true,
    primaryBadgeOnlyForFirst: true,
    onClickGetIt: async recommendation => {
      DomainSearchSuiteModule.internals.bi.onGetItClick == null || DomainSearchSuiteModule.internals.bi.onGetItClick({
        domain: recommendation.domain || '',
        type: recommendation.label,
        position: recommendations == null ? void 0 : recommendations.findIndex(_ref6 => {
          let {
            id
          } = _ref6;
          return id === recommendation.id;
        })
      });
      // Awaited so SearchRecommendation can drive its in-button Loader via isClickLoading.
      if (recommendation.domain) {
        await resolveActionForSuggestion(recommendation.domain);
      }
    },
    Layouts: {
      LabelContainer: LazyRecommendationLabel({
        isLoading: isLoadingLabels,
        matchLabels
      }),
      MobileActions: InlineMobileActions,
      HoverBox: CardHoverBox,
      TitleDisplay: CardTitleDisplay,
      SearchRecommendationContainer: NoPadCardContainer,
      Divider: StableDivider,
      GetItButton: LargeGetItButton
    },
    items: recommendations,
    isLoading: isLoading,
    isPricingLoading: isPricingLoading,
    useMobileLayout: true
  }), hasMore && /*#__PURE__*/React.createElement(Box, {
    align: mobileOptimizations ? 'center' : 'left'
  }, /*#__PURE__*/React.createElement(TextButton, {
    className: s.showMoreButton,
    skin: "dark",
    onClick: fetchNextPage,
    suffixIcon: isFetchingNextPage ? /*#__PURE__*/React.createElement(Box, {
      marginLeft: "3px",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Loader, {
      size: "tiny"
    })) : undefined
  }, t('domain_search_gate.results.show_more')))), /*#__PURE__*/React.createElement(AnonymousFooter, {
    taxesNotIncluded: taxesNotIncluded
  }));
};
//# sourceMappingURL=AlternativesPanel.js.map